import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const fmt = (d) => d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
const displayTime = (time) => {
  if (!time) return '';
  const [hh, mm] = String(time).split(':');
  let h = Number(hh);
  if (Number.isNaN(h)) return time;
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${mm || '00'} ${suffix}`;
};
const timeText = (a) => a?.exact_time ? displayTime(a.exact_time) : String(a?.time_preference || 'exact').replace(/_/g, ' ');

const typeLabel = (a) => a?.appointment_type_label || 'Appointment';
const providerLabel = (a) => a?.provider_type_label || typeLabel(a) || 'Provider';
const providerName = (a) => a?.farrier_name || providerLabel(a);

const FieldIcon = ({ children }) => (
  <span style={{ fontSize: 18, lineHeight: 1, width: 24, textAlign: 'center', opacity: 0.85 }}>{children}</span>
);

const FarrierRespondPage = () => {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggest, setSuggest] = useState({ suggested_date: '', suggested_exact_time: '', notes: '' });
  const [notes, setNotes] = useState({ farrier_general_notes: '', horse_notes: {} });
  const [terms, setTerms] = useState(null);
  const [termsDraft, setTermsDraft] = useState({ provider_instructions: '', cancellation_notice_hours: '' });
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsMsg, setTermsMsg] = useState('');
  const [tplHint, setTplHint] = useState('');
  const TERMS_TEMPLATES = [
    { name: 'The Essentials', text: "Please have your horse caught and waiting 10 minutes before the appointment. Fly spray during summer months is appreciated. Please keep dogs put up and minimize distractions - feeding, turnout, and lessons are best scheduled around our time." },
    { name: 'Full Prep', text: "Please have your horse caught, in a halter, and waiting 10 minutes prior. A clean, dry, level area with good light works best. Fly spray in summer. Please hold off on feeding until we're done - it keeps everyone's attention where it needs to be. Dogs and small children are safest kept clear of the work area." },
    { name: 'Short & Sweet', text: "Horse caught and ready at appointment time, please. Fly spray in summer. Fewer distractions = faster, safer work." },
  ];
  const CXL_TEMPLATES = [
    { name: 'Standard 24', hours: '24', text: "Cancellation policy: cancellations with less than 24 hours notice may be subject to a trip fee." },
    { name: 'Firm 48', hours: '48', text: "Cancellation policy: I plan my route days ahead - cancellations inside 48 hours may be charged a trip fee, and repeat short-notice cancellations may affect future scheduling." },
    { name: 'Flexible', hours: '24', text: "Cancellation policy: life with horses happens - just give me as much notice as you can, and we'll get you rescheduled." },
  ];
  useEffect(() => {
    api.get(`/farrier/respond/${token}/terms`).then(r => {
      setTerms(r.data);
      setTermsDraft({ provider_instructions: r.data.provider_instructions || '', cancellation_notice_hours: r.data.cancellation_notice_hours ? String(r.data.cancellation_notice_hours) : '' });
    }).catch(() => {});
  }, [token]);
  const saveTerms = async () => {
    setTermsSaving(true); setTermsMsg('');
    try {
      await api.post(`/farrier/respond/${token}/terms`, {
        provider_instructions: termsDraft.provider_instructions,
        cancellation_notice_hours: termsDraft.cancellation_notice_hours === '' ? 0 : parseInt(termsDraft.cancellation_notice_hours, 10),
      });
      setTerms(x => ({ ...x, provider_instructions: termsDraft.provider_instructions, cancellation_notice_hours: termsDraft.cancellation_notice_hours ? parseInt(termsDraft.cancellation_notice_hours, 10) : null }));
      setTermsMsg('Saved. These now ride along with every appointment from this barn.');
    } catch (e) { setTermsMsg('Could not save, please try again.'); }
    finally { setTermsSaving(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/farrier/respond/${token}`);
      setAppt(data);
      const horseNotes = {};
      (data.service_groups || []).forEach(g => (g.horses || []).forEach(h => { horseNotes[h.id] = h.farrier_notes || ''; }));
      setNotes({ farrier_general_notes: data.farrier_general_notes || '', horse_notes: horseNotes });
      setSuggest({
        suggested_date: data.suggested_date || data.proposed_date || '',
        suggested_exact_time: data.suggested_exact_time || data.exact_time || '',
        notes: data.suggested_notes || ''
      });
      if (data.status === 'alternate_time_suggested') setShowSuggest(true);
    } catch (e) {
      toast.error('Could not load appointment request');
    } finally {
      setLoading(false);
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const serviceCount = useMemo(() => (appt?.service_groups || []).reduce((sum, g) => sum + (g.horses || []).length, 0), [appt]);

  const openSuggest = () => {
    setSuggest(p => ({
      suggested_date: p.suggested_date || appt?.proposed_date || '',
      suggested_exact_time: p.suggested_exact_time || appt?.exact_time || '',
      notes: p.notes || ''
    }));
    setShowSuggest(true);
  };

  const accept = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/respond/${token}/accept`, {});
      setAppt(data);
      toast.success('Appointment accepted');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to accept'); } finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/respond/${token}/decline`, { reason: declineReason });
      setAppt(data);
      toast.success('Appointment declined');
    } catch (e) { toast.error('Failed to decline'); } finally { setBusy(false); }
  };

  const suggestDate = async () => {
    if (!suggest.suggested_date || !suggest.suggested_exact_time) return toast.error('Choose a date and exact time to suggest');
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/respond/${token}/suggest`, suggest);
      setAppt(data);
      setShowSuggest(true);
      toast.success('Alternate time sent');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to suggest time'); } finally { setBusy(false); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/farrier/respond/${token}/notes`, notes);
      setAppt(data);
      toast.success('Notes saved');
    } catch (e) { toast.error('Failed to save notes'); } finally { setBusy(false); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!appt) return <div className="page-content"><div className="empty-state"><h3>Appointment not found</h3></div></div>;

  return (
    <div>
      <div className="top-bar">
        <h1 style={{ fontSize: 18 }}>{typeLabel(appt)} appointment request</h1>
        <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Connected Rider</p>
      </div>
      <div className="page-content">
        <div className="card" style={{ padding: '14px' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Requested appointment</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>{fmt(appt.proposed_date)} at {timeText(appt)}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>Estimated duration: {appt.duration_minutes || 120} minutes</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{providerLabel(appt)}: {providerName(appt)}</div>
          {appt.location && <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>Location: {appt.location}</div>}
          <div style={{ marginTop: 10 }}><span className="badge badge-amber">{String(appt.status || 'sent').replace(/_/g, ' ')}</span></div>
        </div>

        <div className="section-label">Services</div>
        {(appt.service_groups || []).map(g => (
          <div key={g.id} className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{g.service_label} — {(g.horses || []).length} horse{(g.horses || []).length === 1 ? '' : 's'}</div>
            {g.notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{g.notes}</div>}
            <div style={{ marginTop: 8 }}>
              {(g.horses || []).map(h => (
                <div key={h.id} style={{ padding: '6px 0', borderTop: '1px solid var(--border-color)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{h.horse_name_snapshot}</div>
                  {h.horse_notes && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{h.horse_notes}</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
        {appt.facility_notes && <><div className="section-label">Facility notes</div><div className="card"><p style={{ margin: 0, fontSize: 13 }}>{appt.facility_notes}</p></div></>}
        {appt.message && <><div className="section-label">Message</div><div className="card"><p style={{ margin: 0, fontSize: 13 }}>{appt.message}</p></div></>}

        {terms && (
        <>
          <div className="section-label">My standing instructions</div>
          <div className="card" style={{ padding: '12px 14px', marginBottom: 12 }}>
            {!termsOpen ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {terms.provider_instructions || terms.cancellation_notice_hours
                    ? 'On file for this barn - included with every appointment'
                    : 'Set prep instructions and your cancellation policy for this barn'}
                </span>
                <button className="btn btn-secondary btn-sm" onClick={() => setTermsOpen(true)}>{terms.provider_instructions ? 'Edit' : 'Set up'}</button>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', marginBottom: 4 }}>Prep instructions (shared with this barn on every appointment)</div>
                <select className="form-input" style={{ marginBottom: 6 }} value=""
                  onChange={e => { const tpl = TERMS_TEMPLATES.find(x => x.name === e.target.value); if (tpl) { setTermsDraft(d => ({ ...d, provider_instructions: tpl.text })); setTplHint("Loaded '" + tpl.name + "' - edit below to make it yours."); } }}>
                  <option value="">Choose a template to edit, or write your own below...</option>
                  {TERMS_TEMPLATES.map(x => <option key={x.name} value={x.name}>{x.name}</option>)}
                </select>
                <textarea className="form-input" rows={4} value={termsDraft.provider_instructions}
                  onChange={e => setTermsDraft(d => ({ ...d, provider_instructions: e.target.value }))}
                  placeholder="e.g. Please have your horse brought in 10 minutes prior. Fly spray in summer months. Please minimize distractions - pets, feeding, children - during the appointment." />
                {tplHint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{tplHint}</div>}
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '10px 0 4px' }}>Cancellation notice</div>
                <select className="form-input" style={{ marginBottom: 6 }} value=""
                  onChange={e => { const tpl = CXL_TEMPLATES.find(x => x.name === e.target.value); if (tpl) setTermsDraft(d => ({ ...d,
                    cancellation_notice_hours: tpl.hours,
                    provider_instructions: (d.provider_instructions ? d.provider_instructions.replace(/\n*Cancellation policy:[^]*$/, '').trim() + '\n\n' : '') + tpl.text })); }}>
                  <option value="">Choose a sample policy to edit, or set your own...</option>
                  {CXL_TEMPLATES.map(x => <option key={x.name} value={x.name}>{x.name}</option>)}
                </select>
                <select className="form-input" value={termsDraft.cancellation_notice_hours}
                  onChange={e => setTermsDraft(d => ({ ...d, cancellation_notice_hours: e.target.value }))}>
                  <option value="">No policy</option>
                  <option value="24">24 hours notice</option>
                  <option value="48">48 hours notice</option>
                  <option value="72">72 hours notice</option>
                </select>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button className="btn btn-primary btn-sm" onClick={saveTerms} disabled={termsSaving}>{termsSaving ? 'Saving...' : 'Save'}</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setTermsOpen(false)}>Close</button>
                </div>
                {termsMsg && <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-secondary)' }}>{termsMsg}</div>}
              </>
            )}
          </div>
        </>
      )}
      <div className="section-label">Respond</div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 10 }}>Total horse services: {serviceCount}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <button className="btn btn-primary" onClick={accept} disabled={busy || appt.status === 'accepted'}>Accept</button>
            <button className="btn btn-danger" onClick={decline} disabled={busy || appt.status === 'declined'}>Decline</button>
          </div>
          <button className="btn btn-secondary btn-full" type="button" onClick={openSuggest} disabled={busy}>
            Suggest another time
          </button>
          <input className="form-input" style={{ marginTop: 10 }} value={declineReason} onChange={e => setDeclineReason(e.target.value)} placeholder="Optional decline note" />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            Text shortcut: reply YES to accept, NO to decline, or ALT 7/15 10:30AM to suggest another exact time.
          </div>
        </div>

        {showSuggest && (
          <>
            <div className="section-label">Suggest alternate appointment</div>
            <div className="card">
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Choose a new date and/or time to send back to the appointment requester.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label className="form-label" htmlFor="alternate-date">Alternate date</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border-color)', borderRadius: 10, padding: '0 10px', background: 'var(--surface-color, #fff)' }}>
                    <FieldIcon>📅</FieldIcon>
                    <input
                      id="alternate-date"
                      className="form-input"
                      type="date"
                      value={suggest.suggested_date}
                      onChange={e => setSuggest(p => ({ ...p, suggested_date: e.target.value }))}
                      style={{ border: 0, paddingLeft: 0, background: 'transparent' }}
                    />
                  </div>
                </div>
                <div>
                  <label className="form-label" htmlFor="alternate-time">Alternate time</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border-color)', borderRadius: 10, padding: '0 10px', background: 'var(--surface-color, #fff)' }}>
                    <FieldIcon>🕘</FieldIcon>
                    <input
                      id="alternate-time"
                      className="form-input"
                      type="time"
                      value={suggest.suggested_exact_time}
                      onChange={e => setSuggest(p => ({ ...p, suggested_exact_time: e.target.value }))}
                      style={{ border: 0, paddingLeft: 0, background: 'transparent' }}
                    />
                  </div>
                </div>
              </div>
              <textarea className="form-input" style={{ marginTop: 10 }} rows="2" value={suggest.notes} onChange={e => setSuggest(p => ({ ...p, notes: e.target.value }))} placeholder="Optional note, such as: I can do later that morning" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                <button className="btn btn-secondary" type="button" onClick={() => setShowSuggest(false)} disabled={busy}>Cancel</button>
                <button className="btn btn-primary" type="button" onClick={suggestDate} disabled={busy}>Send alternate</button>
              </div>
            </div>
          </>
        )}

        {appt.status === 'alternate_time_suggested' && appt.suggested_date && appt.suggested_exact_time && (
          <div className="card" style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Suggested time saved for owner approval</div>
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 4 }}>{fmt(appt.suggested_date)} at {displayTime(appt.suggested_exact_time)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>The barn owner can accept this time inside Connected Rider.</div>
          </div>
        )}

        {appt.status === 'accepted' && (
          <>
            <div className="section-label">Add to calendar</div>
            <div className="card" style={{ display: 'grid', gap: 8 }}>
              <a className="btn btn-primary btn-full" href={`/api/farrier/respond/${token}/google-calendar`} target="_blank" rel="noreferrer">Add to Google Calendar</a>
              <a className="btn btn-secondary btn-full" href={`/api/farrier/respond/${token}/calendar.ics`}>Add to Apple / Outlook Calendar</a>
              <a className="btn btn-secondary btn-full" href={`/api/farrier/respond/${token}/calendar.ics`}>Download calendar file</a>
            </div>
          </>
        )}

        <div className="section-label">Completion notes</div>
        <div className="card">
          <div className="form-group">
            <label className="form-label">General visit notes</label>
            <textarea className="form-input" rows="3" value={notes.farrier_general_notes} onChange={e => setNotes(p => ({ ...p, farrier_general_notes: e.target.value }))} />
          </div>
          {(appt.service_groups || []).map(g => (g.horses || []).map(h => (
            <div className="form-group" key={h.id}>
              <label className="form-label">{h.horse_name_snapshot}</label>
              <textarea className="form-input" rows="2" value={notes.horse_notes[h.id] || ''} onChange={e => setNotes(p => ({ ...p, horse_notes: { ...p.horse_notes, [h.id]: e.target.value } }))} placeholder="Horse-specific farrier notes" />
            </div>
          )))}
          <button className="btn btn-primary btn-full" onClick={saveNotes} disabled={busy}>Save notes</button>
        </div>
      </div>
    </div>
  );
};

export default FarrierRespondPage;
